import { useState } from 'react';
import { useTranslation } from '@/src/i18n';
import { Button, Input } from '@/src/presentation/components';
import { Ionicons } from '@expo/vector-icons';
import { Image, KeyboardAvoidingView, Platform, Text, TouchableOpacity, View } from 'react-native';
import { createStyles } from './LoginScreen.styles';

export interface LoginScreenProps {
  email: string;
  setEmail: (value: string) => void;
  password: string;
  setPassword: (value: string) => void;
  loading: boolean;
  handleLogin: () => void;
  navigateToRegister: () => void;
  navigateToForgotPassword: () => void;
  colors: {
    background: string;
    text: string;
    textSecondary: string;
    textTertiary: string;
    card: string;
    border: string;
    primary: string;
    primaryText: string;
    error: string;
  };
}

export const LoginScreen = ({
  email,
  setEmail,
  password,
  setPassword,
  loading,
  handleLogin,
  navigateToRegister,
  navigateToForgotPassword,
  colors,
}: LoginScreenProps) => {
  const styles = createStyles();
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      style={[styles.container, { backgroundColor: colors.background }]}
    >
      <View style={styles.content}>
        <View style={styles.logoContainer}>
          <Image source={require('@/assets/images/android-icon-foreground.png')} style={styles.logoImage} />
        </View>
        <Text style={[styles.title, { color: colors.text }]}>{t('auth.welcomeBack')}</Text>
        <Text style={[styles.subtitle, { color: colors.textSecondary }]}>{t('auth.signInToContinue')}</Text>

        <View style={styles.form}>
          <Input
            placeholder={t('auth.email')}
            value={email}
            onChangeText={setEmail}
            autoCapitalize="none"
            keyboardType="email-address"
            editable={!loading}
            colors={colors}
          />

          <Input
            placeholder={t('auth.password')}
            value={password}
            onChangeText={setPassword}
            secureTextEntry={!showPassword}
            editable={!loading}
            colors={colors}
            rightElement={
              <TouchableOpacity onPress={() => setShowPassword(v => !v)} disabled={loading}>
                <Ionicons
                  name={showPassword ? 'eye-off-outline' : 'eye-outline'}
                  size={20}
                  color={colors.textTertiary}
                />
              </TouchableOpacity>
            }
          />

          <TouchableOpacity style={styles.forgotPassword} onPress={navigateToForgotPassword}>
            <Text style={[styles.forgotPasswordText, { color: colors.primary }]}>{t('auth.forgotPassword')}</Text>
          </TouchableOpacity>

          <Button
            title={t('auth.signIn')}
            onPress={handleLogin}
            disabled={loading}
            loading={loading}
            colors={colors}
          />

          <View style={styles.footer}>
            <Text style={[styles.footerText, { color: colors.textSecondary }]}>{t('auth.dontHaveAccount')} </Text>
            <TouchableOpacity onPress={navigateToRegister}>
              <Text style={[styles.footerLink, { color: colors.primary }]}>{t('auth.signUp')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </KeyboardAvoidingView>
  );
};
